import { useState, useCallback, useEffect } from 'react';
import Sidebar, { type ActiveSection } from './components/Sidebar';
import Dashboard from './components/Dashboard';
import AllApplicants from './components/AllApplicants';
import ApplicantDetail from './components/ApplicantDetail';
import LansmanaKatilanlar from './components/LansmanaKatilanlar';
import PotansiyelBasvurular from './components/PotansiyelBasvurular';
import ManuelEkle from './components/ManuelEkle';
import ExcelYukle from './components/ExcelYukle';
import LeadDrawer from './components/LeadDrawer';
import Reports from './components/Reports';
import Ayarlar from './components/Ayarlar';

import type { Lead } from "../mocks/leads";

const SECTION_LABELS: Record<ActiveSection, string> = {
  dashboard: 'Dashboard',
  tumBasvurular: 'Tüm Başvurular',
  lansmanaKatilanlar: 'Lansmana Katılanlar',
  potansiyelBasvurular: 'Potansiyeller',
  manuelEkle: 'Manuel Ekle',
  excelYukle: 'Excel Yükle',
  raporlar: 'Raporlar',
  ayarlar: 'Ayarlar',
  detay: 'Başvuru Detayı',
};

export default function HomePage() {
  const [active, setActive] = useState<ActiveSection>('dashboard');

  const [tumBasvurular, setTumBasvurular] = useState<Lead[]>([]);
  const [potansiyeller, setPotansiyeller] = useState<Lead[]>([]);
  const [lansmanKatilim, setLansmanKatilim] = useState<Lead[]>([]);

  const [drawerLead, setDrawerLead] = useState<Lead | null>(null);
  const [detailLead, setDetailLead] = useState<Lead | null>(null);

  useEffect(() => {
    fetch("/api/tum_basvurular.php")
      .then(res => res.json())
      .then(data => setTumBasvurular(Array.isArray(data) ? data : []))
      .catch(err => console.error("tum_basvurular.php hata:", err));

    fetch("/api/potansiyeller.php")
      .then(res => res.json())
      .then(data => setPotansiyeller(Array.isArray(data) ? data : []))
      .catch(err => console.error("potansiyeller.php hata:", err));

    fetch("/api/lansmana_katilanlar.php")
      .then(res => res.json())
      .then(data => setLansmanKatilim(Array.isArray(data) ? data : []))
      .catch(err => console.error("lansmana_katilanlar.php hata:", err));
  }, []);

  function findLeadById(id: string): Lead | null {
    return (
      tumBasvurular.find((l) => l.id === id) ||
      potansiyeller.find((l) => l.id === id) ||
      lansmanKatilim.find((l) => l.id === id) ||
      null
    );
  }

  const updateLead = useCallback((id: string, patch: Partial<Lead>) => {
    setTumBasvurular((prev) => prev.map((l) => l.id === id ? { ...l, ...patch } : l));
    setPotansiyeller((prev) => prev.map((l) => l.id === id ? { ...l, ...patch } : l));
    setLansmanKatilim((prev) => prev.map((l) => l.id === id ? { ...l, ...patch } : l));

    setDrawerLead((prev) => prev?.id === id ? { ...prev, ...patch } as Lead : prev);
    setDetailLead((prev) => prev?.id === id ? { ...prev, ...patch } as Lead : prev);
  }, []);

  const bulkUpdate = useCallback((ids: string[], patch: Partial<Lead>) => {
    setTumBasvurular((prev) => prev.map((l) => ids.includes(l.id) ? { ...l, ...patch } : l));
    setPotansiyeller((prev) => prev.map((l) => ids.includes(l.id) ? { ...l, ...patch } : l));
    setLansmanKatilim((prev) => prev.map((l) => ids.includes(l.id) ? { ...l, ...patch } : l));
  }, []);

  function openDrawer(lead: Lead) {
    const fresh = findLeadById(lead.id) || lead;
    setDrawerLead(fresh);
  }

  function handleNav(section: ActiveSection) {
    if (section !== 'detay') setDetailLead(null);
    setActive(section);
  }

  function handleViewDetailLegacy(lead: Lead) {
    const fresh = findLeadById(lead.id) || lead;
    setDetailLead(fresh);
    setActive('detay');
  }

  function handleBack() {
    setDetailLead(null);
    setActive('tumBasvurular');
  }

  const freshDrawerLead = drawerLead ? (findLeadById(drawerLead.id) || drawerLead) : null;
  const freshDetailLead = detailLead ? (findLeadById(detailLead.id) || detailLead) : null;

  return (
    <div className="min-h-screen bg-[#060910] flex font-sans">
      <Sidebar active={active} onChange={handleNav} />

      <main className="flex-1 ml-60 min-h-screen overflow-y-auto transition-all duration-300">
        <header className="sticky top-0 z-20 flex items-center justify-between px-8 py-4 border-b border-white/[0.05] bg-[#060910]/90 backdrop-blur-xl">
          <div className="flex items-center gap-2 text-white/30 text-xs">
            <i className="ri-home-4-line" />
            <span>/</span>
            <span className="text-white/60">{SECTION_LABELS[active]}</span>
          </div>

          <div className="flex items-center gap-3">
            <div className="flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-emerald-500/10 border border-emerald-500/20">
              <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" />
              <span className="text-emerald-400 text-xs font-semibold">Canlı</span>
            </div>

            <div className="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-white/[0.04] border border-white/[0.06]">
              <i className="ri-calendar-line text-white/30 text-sm" />
              <span className="text-white/40 text-xs">
                {new Date().toLocaleDateString('tr-TR', {
                  day: 'numeric',
                  month: 'long',
                  year: 'numeric'
                })}
              </span>
            </div>

            <button
              onClick={() => setActive('manuelEkle')}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-amber-500/15 hover:bg-amber-500/25 text-amber-400 text-xs font-semibold transition-all cursor-pointer whitespace-nowrap"
            >
              <i className="ri-user-add-line" />Ekle
            </button>
          </div>
        </header>

        <div className="px-8 py-7">
          {active === 'dashboard' && (
            <Dashboard
              leads={tumBasvurular}
              onViewDetail={handleViewDetailLegacy}
              onOpenDrawer={openDrawer}
            />
          )}

          {active === 'tumBasvurular' && (
            <AllApplicants
              leads={tumBasvurular}
              onOpenDrawer={openDrawer}
              onBulkUpdate={bulkUpdate}
            />
          )}

          {active === 'lansmanaKatilanlar' && (
            <LansmanaKatilanlar
              leads={lansmanKatilim}
              onViewDetail={(l) => openDrawer(l)}
            />
          )}

          {active === 'potansiyelBasvurular' && (
            <PotansiyelBasvurular
              leads={potansiyeller}
              onViewDetail={(l) => openDrawer(l)}
            />
          )}

          {active === 'manuelEkle' && <ManuelEkle />}
          {active === 'excelYukle' && <ExcelYukle />}
          {active === 'raporlar' && <Reports leads={tumBasvurular} />}
          {active === 'ayarlar' && <Ayarlar />}

          {active === 'detay' && freshDetailLead && (
            <ApplicantDetail
              lead={freshDetailLead}
              onBack={handleBack}
            />
          )}
        </div>
      </main>

      {freshDrawerLead && (
        <LeadDrawer
          lead={freshDrawerLead}
          onClose={() => setDrawerLead(null)}
          onUpdate={updateLead}
        />
      )}
    </div>
  );
}